import { Check } from 'lucide-react';

export interface WizardStep {
  id: number;
  label: string;
  shortLabel?: string;
}

interface WizardStepperProps {
  steps: WizardStep[];
  currentStep: number;
  maxReachedStep: number;
  onStepClick: (stepId: number) => void;
  isMobile: boolean;
}

export default function WizardStepper({
  steps,
  currentStep,
  maxReachedStep,
  onStepClick,
  isMobile,
}: WizardStepperProps) {
  const active = steps.find((s) => s.id === currentStep);
  const totalSteps = steps.length;
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  if (isMobile) {
    return (
      <div
        role="progressbar"
        aria-valuenow={currentStep + 1}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-label={`Étape ${currentStep + 1} sur ${totalSteps}`}
        className="bg-white rounded-2xl shadow-sm px-3 py-2.5 sm:px-4 sm:py-3"
      >
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-[11px] font-semibold tracking-wider text-emerald-600">
            ÉTAPE {currentStep + 1} / {totalSteps}
          </span>
          <span className="text-xs font-semibold text-gray-900 text-right max-w-[60%] truncate">
            {active?.label}
          </span>
        </div>
        <div className="flex items-center gap-1">
          {steps.map((step) => {
            const isDone = step.id < currentStep;
            const isCurrent = step.id === currentStep;
            return (
              <div
                key={step.id}
                className={`flex-1 h-1 rounded-full transition-colors ${
                  isDone || isCurrent ? 'bg-emerald-500' : 'bg-gray-200'
                }`}
              />
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <nav aria-label="Progression du simulateur" className="bg-white rounded-2xl shadow-sm px-4 py-3 lg:px-5 lg:py-3.5">
      <ol className="flex items-center">
        {steps.map((step, idx) => {
          const isDone = step.id < currentStep;
          const isCurrent = step.id === currentStep;
          const isClickable = step.id <= maxReachedStep;
          const isLast = idx === steps.length - 1;

          return (
            <li key={step.id} className={`flex items-center ${isLast ? '' : 'flex-1'}`}>
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step.id)}
                disabled={!isClickable}
                aria-current={isCurrent ? 'step' : undefined}
                className={`flex items-center gap-3 group text-left ${
                  isClickable ? 'cursor-pointer' : 'cursor-not-allowed'
                }`}
              >
                <span
                  className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-all ${
                    isCurrent
                      ? 'bg-emerald-600 text-white shadow-sm'
                      : isDone
                      ? 'bg-emerald-600 text-white'
                      : 'bg-white border-2 border-gray-200 text-gray-400'
                  }`}
                >
                  {isDone ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : step.id + 1}
                </span>
                <span
                  className={`text-xs font-semibold whitespace-nowrap ${
                    isCurrent ? 'text-gray-900' : isDone ? 'text-gray-700' : 'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </button>
              {!isLast && (
                <div
                  className={`flex-1 h-px mx-3 ${
                    isDone ? 'bg-emerald-500' : 'bg-gray-200'
                  }`}
                  aria-hidden="true"
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
